import { getPublicRuntimeConfig } from '../../../libs/init/getPublicRuntimeConfig';

/** Strips a trailing slash but preserves a lone "/". */
const stripTrailingSlash = (path: string): string =>
  path.length > 1 ? path.replace(/\/+$/u, '') : path;

/**
 * Joins the app base prefix (e.g. `/v2`) with a router-relative path
 * (e.g. `/call`) into a full `window.location.pathname` (e.g. `/v2/call`).
 */
const withBase = (base: string, route: string): string =>
  stripTrailingSlash(`${base === '/' ? '' : stripTrailingSlash(base)}${route}`);

/**
 * Non-React check for whether the current route is the call experience, used
 * only to gate chunk-reload recovery (see `setChunkReloadActiveCallGuard`).
 *
 * The guard runs outside React — in the `vite:preloadError` window listener and
 * in error-boundary `componentDidCatch` — so we read `window.location` directly
 * rather than the router or any hook.
 *
 * Being on an in-call route is equivalent to having a live call in this tab:
 * media is torn down when leaving the call route (see
 * `useStopLocalMediaBasedOnRoute`), and call / hold / test-call are the only
 * routes that host a live session. This intentionally avoids reading the call
 * state machine: its `idle` state is set once on app load and is never returned
 * to, so a "state !== idle" check would be permanently true after the first
 * call.
 *
 * @returns `true` when the current path is the call, call-hold, or test-call route.
 */
export const isInActiveCall = (): boolean => {
  const { paths } = getPublicRuntimeConfig();
  const current = stripTrailingSlash(window.location.pathname);
  return [paths.call, paths.callHold, paths.testCall].some(
    (route) => current === withBase(paths.base, route),
  );
};
